/**
 * Deal Pipeline Page - Phase 3
 * Comprehensive deal management with filtering, sorting, and highlights
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  FunnelSimple,
  SortAscending,
  Plus,
  MagnifyingGlass,
  GridFour,
  List,
  Warning,
  Star,
  TrendUp,
  Lightning,
  X,
  CaretDown,
  ArrowUp,
  ArrowDown,
} from '@phosphor-icons/react';
import { DealCard, LoadingPage, EmptyState, StatusBadge } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { dealsApi, partnersApi } from '../utils/api';
import { 
  formatCurrency, 
  formatPercent,
  formatArea,
  getStatusConfig,
  getStatusOptions,
  getRiskConfig,
  isNegotiationDeal,
  DEAL_STAGES,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Slider } from '../components/ui/slider';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { cn } from '../lib/utils';

// Paris arrondissements
const ARRONDISSEMENTS = [
  '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th',
  '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th'
];

// Sort options
const SORT_OPTIONS = [
  { value: 'created_at', label: 'Plus récents' },
  { value: 'deal_name', label: 'Nom (A-Z)' },
  { value: 'roi_desc', label: 'Rendement (Décroissant)' },
  { value: 'roi_asc', label: 'Rendement (Croissant)' },
  { value: 'cost_desc', label: 'Coût (Décroissant)' },
  { value: 'cost_asc', label: 'Coût (Croissant)' },
  { value: 'status', label: 'Étape Pipeline' },
];

// Risk level options
const RISK_OPTIONS = [
  { value: 'all', label: 'Tous les risques' },
  { value: 'low', label: 'Risque Faible' },
  { value: 'medium', label: 'Risque Moyen' },
  { value: 'high', label: 'Risque Élevé' },
  { value: 'critical', label: 'Risque Critique' },
];

export const DealPipeline = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [partners, setPartners] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [arrondissementFilter, setArrondissementFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [roiRange, setRoiRange] = useState([-50, 100]);
  const [sortBy, setSortBy] = useState('status');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dealsData, partnersData] = await Promise.all([
          dealsApi.getAll(),
          partnersApi.getAll(),
        ]);
        setDeals(dealsData);
        setPartners(partnersData);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get partner name by ID
  const getPartnerName = (partnerId) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner?.name || '—';
  };

  // Filter and sort deals
  const filteredDeals = useMemo(() => {
    let result = [...deals];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.deal_name.toLowerCase().includes(query) ||
        d.property?.address?.toLowerCase().includes(query) ||
        d.property?.arrondissement?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }

    // Arrondissement filter
    if (arrondissementFilter !== 'all') {
      result = result.filter(d => d.property?.arrondissement === arrondissementFilter);
    }

    // Partner filter
    if (partnerFilter !== 'all') {
      result = result.filter(d => 
        d.assigned_partner_id === partnerFilter || 
        d.sourced_by_id === partnerFilter
      );
    }

    // Risk filter
    if (riskFilter !== 'all') {
      result = result.filter(d => d.risk_level === riskFilter);
    }

    // ROI range filter
    result = result.filter(d => {
      const roi = d.expected_roi_percent ?? 0;
      return roi >= roiRange[0] && roi <= roiRange[1];
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'deal_name':
          return a.deal_name.localeCompare(b.deal_name);
        case 'roi_desc':
          return (b.expected_roi_percent ?? -999) - (a.expected_roi_percent ?? -999);
        case 'roi_asc':
          return (a.expected_roi_percent ?? 999) - (b.expected_roi_percent ?? 999);
        case 'cost_desc':
          return (b.total_project_cost ?? 0) - (a.total_project_cost ?? 0);
        case 'cost_asc':
          return (a.total_project_cost ?? 0) - (b.total_project_cost ?? 0);
        case 'status':
          return (getStatusConfig(a.status).order ?? 99) - (getStatusConfig(b.status).order ?? 99);
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return result;
  }, [deals, searchQuery, statusFilter, arrondissementFilter, partnerFilter, riskFilter, roiRange, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    const active = deals.filter(d => !['sold', 'dead'].includes(d.status));
    const negotiating = deals.filter(d => isNegotiationDeal(d.status));
    const atRisk = deals.filter(d => d.risk_level === 'high' || d.risk_level === 'critical');
    const topPerformers = deals.filter(d => (d.expected_roi_percent ?? 0) >= 20);

    return {
      total: deals.length,
      active: active.length,
      negotiating: negotiating.length,
      atRisk: atRisk.length,
      topPerformers: topPerformers.length,
    };
  }, [deals]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setArrondissementFilter('all');
    setPartnerFilter('all');
    setRiskFilter('all');
    setRoiRange([-50, 100]);
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter !== 'all' || arrondissementFilter !== 'all' || 
    partnerFilter !== 'all' || riskFilter !== 'all' || roiRange[0] !== -50 || roiRange[1] !== 100;

  // Highlight flags for deals
  const getDealFlags = (deal) => {
    const flags = [];
    
    // Top performer (ROI >= 20%)
    if ((deal.expected_roi_percent ?? 0) >= 20) {
      flags.push({ type: 'top', label: 'Top Performer', icon: Star, color: 'text-amber-500' });
    }
    
    // At risk
    if (deal.risk_level === 'high' || deal.risk_level === 'critical') {
      flags.push({ type: 'risk', label: 'À risque', icon: Warning, color: 'text-red-500' });
    }
    
    // Urgent negotiation
    if (isNegotiationDeal(deal.status)) {
      flags.push({ type: 'urgent', label: 'Urgent', icon: Lightning, color: 'text-violet-500' });
    }
    
    return flags;
  };

  if (loading) return <LoadingPage message="Chargement du pipeline..." />;

  return (
    <div data-testid="deal-pipeline" className="space-y-6 animate-fade-in">
      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card-premium text-center py-4">
          <p className="text-2xl font-bold font-tabular">{stats.total}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Deals</p>
        </div>
        <div className="card-premium text-center py-4">
          <p className="text-2xl font-bold font-tabular text-emerald-600">{stats.active}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Actifs</p>
        </div>
        <div className="card-premium text-center py-4">
          <p className="text-2xl font-bold font-tabular text-violet-600">{stats.negotiating}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">En Négociation</p>
        </div>
        <div className="card-premium text-center py-4">
          <p className="text-2xl font-bold font-tabular text-amber-600">{stats.topPerformers}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Top Rendement</p>
        </div>
        <div className="card-premium text-center py-4">
          <p className="text-2xl font-bold font-tabular text-red-600">{stats.atRisk}</p>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">À Risque</p>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="card-premium p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Primary row: Search and filters - Mobile optimized */}
          <div className="flex flex-col gap-3">
            {/* Search - Full width on mobile */}
            <div className="relative">
              <MagnifyingGlass 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
              />
              <Input
                data-testid="deal-search-input"
                placeholder="Rechercher des deals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full sm:w-56"
              />
            </div>
            
            {/* Filter row - scrollable on mobile */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-wrap">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-32 sm:w-40 flex-shrink-0">
                  <SelectValue placeholder="Étape" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les étapes</SelectItem>
                  {getStatusOptions().map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Arrondissement Filter - Hidden on smallest screens */}
              <Select value={arrondissementFilter} onValueChange={setArrondissementFilter}>
                <SelectTrigger data-testid="arr-filter" className="w-28 sm:w-40 flex-shrink-0">
                  <SelectValue placeholder="Arr." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les Arr.</SelectItem>
                  {ARRONDISSEMENTS.map(arr => (
                    <SelectItem key={arr} value={arr}>
                      {arr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* More Filters Toggle */}
              <Button
                variant={showFilters ? "secondary" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="more-filters-btn"
                className="flex-shrink-0"
              >
                <FunnelSimple size={16} className="sm:mr-1" />
                <span className="hidden sm:inline">Plus</span>
                <CaretDown size={14} className={cn("ml-1 transition-transform", showFilters && "rotate-180")} />
              </Button>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground flex-shrink-0"
                  data-testid="clear-filters-btn"
                >
                  <X size={14} />
                  <span className="hidden sm:inline ml-1">Effacer</span>
                </Button>
              )}
            </div>

            {/* Action row - Sort, View toggle, Add */}
            <div className="flex items-center justify-between gap-2">
              {/* Sort - Compact on mobile */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-select" className="w-32 sm:w-44">
                  <SortAscending size={16} className="mr-1 sm:mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                {/* View Toggle */}
                <div className="flex items-center border rounded-lg p-1">
                  <button
                    data-testid="view-grid-btn"
                    onClick={() => setViewMode('grid')}
                    className={cn(
                      'p-1.5 rounded transition-colors',
                      viewMode === 'grid' ? 'bg-secondary' : 'hover:bg-secondary/50'
                    )}
                  >
                    <GridFour size={18} />
                  </button>
                  <button
                    data-testid="view-table-btn"
                    onClick={() => setViewMode('table')}
                    className={cn(
                      'p-1.5 rounded transition-colors',
                      viewMode === 'table' ? 'bg-secondary' : 'hover:bg-secondary/50'
                    )}
                  >
                    <List size={18} />
                  </button>
                </div>

                {/* Add Deal Button */}
                <Button 
                  data-testid="add-deal-btn"
                  className="bg-[#3A4A40] hover:bg-[#2E3B33]"
                  onClick={() => navigate('/deal/new')}
                  size="sm"
                >
                  <Plus size={18} />
                  <span className="hidden sm:inline ml-1">Nouveau Deal</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Expanded Filters Row */}
          {showFilters && (
            <div className="flex flex-wrap items-center gap-4 pt-4 border-t animate-fade-in">
              {/* Partner Filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Partenaire</label>
                <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                  <SelectTrigger data-testid="partner-filter" className="w-44">
                    <SelectValue placeholder="Tous les partenaires" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les partenaires</SelectItem>
                    {partners.map(partner => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Risk Filter */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-muted-foreground">Niveau de Risque</label>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger data-testid="risk-filter" className="w-40">
                    <SelectValue placeholder="Tous les risques" />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ROI Range */}
              <div className="flex flex-col gap-1 min-w-[200px]">
                <label className="text-xs text-muted-foreground">
                  Plage Rendement: {roiRange[0]}% - {roiRange[1]}%
                </label>
                <Slider
                  data-testid="roi-slider"
                  value={roiRange}
                  onValueChange={setRoiRange}
                  min={-50}
                  max={100}
                  step={5}
                  className="mt-2"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between px-1">
        <p className="text-sm text-muted-foreground">
          Affichage de <span className="font-medium text-foreground">{filteredDeals.length}</span> sur {deals.length} deals
        </p>
      </div>

      {/* Deals Display */}
      {filteredDeals.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="Aucun deal trouvé"
          description={hasActiveFilters
            ? "Essayez d'ajuster vos filtres."
            : "Ajoutez votre premier deal pour commencer."}
          actionLabel="Ajouter un Deal"
          onAction={() => navigate('/deal/new')}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {filteredDeals.map((deal) => {
            const flags = getDealFlags(deal);
            return (
              <Link 
                key={deal.id} 
                to={`/deal/${deal.id}`}
                className="card-premium p-3 sm:p-4 hover:shadow-lg transition-all duration-200 group"
                data-testid={`deal-card-${deal.id}`}
              >
                {/* Flags */}
                {flags.length > 0 && (
                  <div className="flex gap-1 mb-2">
                    {flags.map((flag, idx) => (
                      <flag.icon key={idx} size={16} weight="fill" className={flag.color} title={flag.label} />
                    ))}
                  </div>
                )}
                
                <h3 className="font-semibold text-sm sm:text-base group-hover:text-primary transition-colors line-clamp-1">
                  {deal.deal_name}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-1">
                  {deal.property?.address}
                </p>
                
                <div className="flex items-center gap-2 mt-2">
                  <StatusBadge status={deal.status} />
                  <span className="text-xs text-muted-foreground">{deal.property?.arrondissement}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Project Cost</p>
                    <p className="font-semibold font-tabular text-xs sm:text-sm">
                      {formatCurrency(deal.total_project_cost)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Expected ROI</p>
                    <p className={cn(
                      'font-semibold font-tabular text-xs sm:text-sm',
                      (deal.expected_roi_percent ?? 0) >= 20 ? 'text-emerald-600' :
                      (deal.expected_roi_percent ?? 0) >= 10 ? 'text-lime-600' :
                      (deal.expected_roi_percent ?? 0) >= 0 ? 'text-amber-600' : 'text-red-600'
                    )}>
                      {formatPercent(deal.expected_roi_percent)}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        /* Table View - Desktop only, Cards on Mobile */
        <>
          {/* Desktop Table */}
          <div className="card-premium p-0 overflow-hidden hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/30">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Nom du Deal</TableHead>
                  <TableHead>Adresse</TableHead>
                  <TableHead>Arr.</TableHead>
                  <TableHead>Étape</TableHead>
                  <TableHead>Partenaire</TableHead>
                  <TableHead className="text-right">Surface</TableHead>
                  <TableHead className="text-right">Coût du Projet</TableHead>
                  <TableHead className="text-right">ROI Attendu</TableHead>
                  <TableHead className="text-center">Risque</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeals.map((deal) => {
                  const flags = getDealFlags(deal);
                  const riskConfig = getRiskConfig(deal.risk_level, deal.risk_score);
                  
                  return (
                    <TableRow 
                      key={deal.id} 
                      data-testid={`deal-row-${deal.id}`}
                      className={cn(
                        "hover:bg-secondary/20 transition-colors",
                        flags.some(f => f.type === 'risk') && "bg-red-50/50",
                        flags.some(f => f.type === 'top') && "bg-amber-50/30"
                      )}
                    >
                      <TableCell className="w-8">
                        <div className="flex gap-0.5">
                          {flags.map((flag, idx) => (
                            <flag.icon key={idx} size={14} weight="fill" className={flag.color} title={flag.label} />
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link 
                          to={`/deal/${deal.id}`}
                          className="font-medium hover:text-primary transition-colors"
                        >
                        {deal.deal_name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {deal.property?.address}
                    </TableCell>
                    <TableCell>{deal.property?.arrondissement}</TableCell>
                    <TableCell>
                      <StatusBadge status={deal.status} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {getPartnerName(deal.assigned_partner_id || deal.sourced_by_id)}
                    </TableCell>
                    <TableCell className="text-right font-tabular">
                      {formatArea(deal.property?.surface_sqm)}
                    </TableCell>
                    <TableCell className="text-right font-tabular">
                      {formatCurrency(deal.total_project_cost)}
                    </TableCell>
                    <TableCell className={cn(
                      'text-right font-tabular font-medium',
                      (deal.expected_roi_percent ?? 0) >= 20 ? 'text-emerald-600' :
                      (deal.expected_roi_percent ?? 0) >= 10 ? 'text-lime-600' :
                      (deal.expected_roi_percent ?? 0) >= 0 ? 'text-amber-600' : 'text-red-600'
                    )}>
                      {formatPercent(deal.expected_roi_percent)}
                    </TableCell>
                    <TableCell className="text-center">
                      {deal.risk_level && (
                        <Badge variant="secondary" className={cn("text-xs", riskConfig.color)}>
                          {deal.risk_level}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

          {/* Mobile Card List */}
          <div className="lg:hidden space-y-3">
            {filteredDeals.map((deal) => {
              const flags = getDealFlags(deal);
              const riskConfig = getRiskConfig(deal.risk_level, deal.risk_score);
              
              return (
                <Link
                  key={deal.id}
                  to={`/deal/${deal.id}`}
                  className="block card-premium p-4 hover:shadow-md transition-all active:bg-secondary/50"
                  data-testid={`deal-mobile-card-${deal.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1 mb-1">
                        {flags.map((flag, idx) => (
                          <flag.icon key={idx} size={14} weight="fill" className={flag.color} />
                        ))}
                      </div>
                      <h3 className="font-semibold text-sm truncate">{deal.deal_name}</h3>
                      <p className="text-xs text-muted-foreground truncate">{deal.property?.address}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <StatusBadge status={deal.status} />
                      {deal.risk_level && (
                        <Badge variant="secondary" className={cn("text-[10px]", riskConfig.color)}>
                          {deal.risk_level}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Metrics Grid */}
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Arr.</p>
                      <p className="font-medium">{deal.property?.arrondissement || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Surface</p>
                      <p className="font-medium font-tabular">{deal.property?.surface_sqm || '—'} m²</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Cost</p>
                      <p className="font-medium font-tabular">{formatCurrency(deal.total_project_cost)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">ROI</p>
                      <p className={cn(
                        'font-bold font-tabular',
                        (deal.expected_roi_percent ?? 0) >= 20 ? 'text-emerald-600' :
                        (deal.expected_roi_percent ?? 0) >= 10 ? 'text-lime-600' :
                        (deal.expected_roi_percent ?? 0) >= 0 ? 'text-amber-600' : 'text-red-600'
                      )}>
                        {formatPercent(deal.expected_roi_percent)}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="deal-pipeline" 
        content={pageExplanations.dealPipeline} 
      />
    </div>
  );
};

export default DealPipeline;
